// Silent re-authentication for the Recharge storefront session.
//
// The Recharge `apiToken` is short-lived and was previously never renewed: once
// it lapsed the member was bounced to /login, losing whatever they were in the
// middle of. The Shopify customer access token we already keep in localStorage
// outlives it by a long way, and trading it for a fresh Recharge session needs
// no interaction from the member — so we do that instead of logging them out.
//
// Everything here is best-effort: any path that can't produce a session returns
// null and the caller falls back to the pre-existing logout behaviour.

import { loginWithShopifyStorefront } from '../services/recharge';
import { mintStorefrontToken } from '../services/api';
import { readRechargeApiToken } from '../utils/hardeningCanary';
import { customerAccessTokenRenew, getCustomer as getShopifyCustomer } from '../services/shopify';

const RECHARGE_SESSION_KEY = 'rechargeSession';
const SHOPIFY_TOKEN_KEY = 'shopifyCustomerAccessToken';

// Gates the proactive background poll only (see canRefreshSession) — not the
// reactive refresh in refreshRechargeSession().
const MAX_CONSECUTIVE_FAILURES = 2;

// Shopify issues these with a multi-day lifetime, so a couple of days of
// headroom keeps renewal well clear of the expiry cliff.
const RENEW_THRESHOLD_MS = 2 * 24 * 60 * 60 * 1000;

let inFlightRefresh = null;
let failureCount = 0;

const readShopifyToken = () => {
  try {
    const raw = localStorage.getItem(SHOPIFY_TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn('[sessionRefresh] Unable to read Shopify customer token', err);
    return null;
  }
};

const writeShopifyToken = (accessToken, expiresAt) => {
  try {
    localStorage.setItem(SHOPIFY_TOKEN_KEY, JSON.stringify({ accessToken, expiresAt }));
  } catch (err) {
    console.warn('[sessionRefresh] Unable to persist Shopify customer token', err);
  }
};

// A token with no recorded expiry counts as usable — some flows stored one
// before we started stamping `expiresAt`, and letting Recharge reject it beats
// refusing to try.
const isUsable = (token) => {
  if (!token?.accessToken) return false;
  if (!token.expiresAt) return true;
  const expiry = Date.parse(token.expiresAt);
  return Number.isNaN(expiry) || expiry > Date.now();
};

/** Whether it's worth proactively polling for a session right now (background validator only). */
export const canRefreshSession = () =>
  failureCount < MAX_CONSECUTIVE_FAILURES && isUsable(readShopifyToken());

/**
 * Exchange the stored Shopify customer token for a fresh Recharge session and
 * persist it. Resolves to the new session, or null when no silent refresh is
 * possible — callers must treat null as "fall back to logout".
 */
export const refreshRechargeSession = async () => {
  // Callers arriving mid-refresh share the result rather than starting a second
  // login, so a burst of parallel 401s costs one round trip.
  if (inFlightRefresh) return inFlightRefresh;
  if (!isUsable(readShopifyToken())) return null;

  const { accessToken } = readShopifyToken();

  inFlightRefresh = (async () => {
    try {
      const session = await loginWithShopifyStorefront(
        process.env.REACT_APP_SHOPIFY_STOREFRONT_ACCESS_TOKEN,
        accessToken
      );

      // The SDK resolves to null rather than throwing when Recharge declines to
      // issue a token for this customer. It also answers a Shopify token that
      // Shopify no longer honours (e.g. after a password reset) with HTTP 200,
      // an api_token, customer_id: null and message "Error occurred in call to
      // Shopify." — a token with no customer behind it. Persisting that would
      // make every customer-scoped SDK read throw "Not logged in." before any
      // request is sent, so the member is stuck on "Couldn't load this page"
      // with no 401 to trigger another refresh. Treat it as a failed refresh.
      if (!session?.apiToken || !session?.customerId) {
        failureCount += 1;
        if (session?.apiToken) {
          console.warn('[sessionRefresh] Recharge returned a session without a customer', session.message);
        }
        return null;
      }

      try {
        localStorage.setItem(RECHARGE_SESSION_KEY, JSON.stringify(session));
      } catch (err) {
        // Storage failed but the session is good — hand it back so the request
        // that triggered the refresh still succeeds.
        console.warn('[sessionRefresh] Unable to persist refreshed session', err);
      }

      failureCount = 0;
      return session;
    } catch (err) {
      failureCount += 1;
      console.warn('[sessionRefresh] Silent session refresh failed', err);
      return null;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
};

/**
 * Ensure a usable Shopify customer token exists, minting one via the API's
 * /api/auth/storefront-token. The one-time-code login path
 * authenticates against Recharge only, so without this those members have no
 * long-lived credential to refresh from.
 */
export const ensureShopifyCustomerToken = async ({ email } = {}) => {
  if (!email) return null;
  const existing = readShopifyToken();
  if (isUsable(existing)) {
    // A token left on a shared device by someone else's session must not be
    // reused: silent refresh would sign this member into that account. Keep it
    // only when it provably belongs to the member logging in; otherwise mint a
    // fresh one from their own Recharge session.
    let owner = null;
    try {
      owner = await getShopifyCustomer(existing.accessToken);
    } catch (err) {
      owner = null;
    }
    if (owner?.email && owner.email.trim().toLowerCase() === email.trim().toLowerCase()) return existing;
    try {
      localStorage.removeItem(SHOPIFY_TOKEN_KEY);
    } catch (err) {
      // Nothing more to do; the mint below overwrites it on success.
    }
  }

  try {
    // Prove the live Recharge session to the API and let it mint the Shopify
    // token — no admin credential in the browser.
    const result = await mintStorefrontToken(readRechargeApiToken());
    if (!result?.storefront_customer_access_token) return null;

    writeShopifyToken(result.storefront_customer_access_token, result.token_expires_at);
    return readShopifyToken();
  } catch (err) {
    console.warn('[sessionRefresh] Unable to mint a Shopify customer token', err);
    return null;
  }
};

/**
 * Extend the Shopify customer token, which sets the ceiling on how long silent
 * refresh keeps working. Never discards the existing token: if renewal fails we
 * keep what we have until it lapses, which is the behaviour we had before.
 */
export const maybeRenewShopifyToken = async () => {
  const token = readShopifyToken();
  if (!isUsable(token) || !token.expiresAt) return;

  const expiry = Date.parse(token.expiresAt);
  if (Number.isNaN(expiry) || expiry - Date.now() > RENEW_THRESHOLD_MS) return;

  const renewed = await customerAccessTokenRenew(token.accessToken);
  if (renewed?.accessToken) writeShopifyToken(renewed.accessToken, renewed.expiresAt);
};
